import React from "react";

/**
 * Msg 234 §3 · Lovable-style tab bar.
 * Full-width row, transparent background, 1px bottom hairline.
 * Tabs are plain text buttons evenly spread. Active tab has a 2px cream
 * underline that sits on the hairline and is the width of the label.
 * Yellow is reserved (score / primary CTAs) so we never use it here.
 *
 * Props:
 *   tabs   : Array<{ k: string, label: string }>
 *   active : string  · matches `k`
 *   onChange(k)
 *   testId : optional root data-testid (defaults to "lovable-tabs")
 */
export default function LovableTabs({ tabs, active, onChange, testId = "lovable-tabs" }) {
  return (
    <div data-testid={testId}
         style={{
           display: "flex",
           width: "100%",
           alignItems: "stretch",
           position: "relative",
           borderBottom: "1px solid rgba(245, 239, 230, 0.14)", // cream hairline @ low opacity
         }}>
      {tabs.map(({ k, label, Icon }) => {
        const isActive = active === k;
        return (
          <button
            key={k}
            type="button"
            data-testid={`lovable-tab-${k}`}
            onClick={() => onChange(k)}
            style={{
              flex: 1,
              minHeight: 46,
              padding: "12px 4px",
              background: "transparent",
              border: 0,
              cursor: "pointer",
              color: isActive ? "var(--ww-text)" : "var(--ww-text-muted)",
              fontFamily: "Poppins",
              fontWeight: isActive ? 600 : 500,
              fontSize: 14,
              lineHeight: 1.1,
              textTransform: "lowercase",
              position: "relative",
              display: "inline-flex",
              flexDirection: Icon ? "column" : "row",
              alignItems: "center",
              justifyContent: "center",
              gap: Icon ? 4 : 6,
              letterSpacing: "-0.005em",
            }}>
            {Icon && (
              // WW-146.c · ink line icon, light Kili fill; full yellow on the selected tab
              <Icon size={22} strokeWidth={1.9} color="currentColor" fill="#FFD60A"
                    fillOpacity={isActive ? 1 : 0.45} aria-hidden="true" data-testid={`lovable-tab-icon-${k}`} />
            )}
            <span style={{ position: "relative", display: "inline-block", paddingBottom: 2 }}>
              {label}
              {isActive && (
                <span aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: -13,  // sit on the hairline
                        height: 2,
                        background: "var(--ww-text)",
                        borderRadius: 2,
                      }} />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
